/**
 * CS's chat tag glossary as the console reads it: ant's `chatTagGlossary`
 * (categories with their tags, retired included, rider support only), held
 * in React Query once per page load, plus every lookup the support pages
 * make on a tag id.
 *
 * What a session stores is the tag's slug (`lock-issues.bike-wont-unlock`):
 * readable, unique, never changed, while the label CS sees is editable. Old
 * strings earlier consoles sent (`billing`, `unlock`, ...) are not glossary
 * ids: they show with their old labels, count as they are and can be
 * removed, never added again (LEGACY_CHAT_TAG_LABELS).
 *
 * ant's GraphQL enums are uppercase (RIDER_SUPPORT, KAYAK); this module (and
 * hooks/chat/hardwareFamily.ts, which it re-exports) is where they are mapped.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AntChatTag,
  AntChatTagCategory,
  AntChatTagGlossary,
  fetchChatTagGlossary,
} from '../../hooks/chat/api';
import {
  FAMILY_LABELS,
  HARDWARE_FAMILIES,
  HardwareFamily,
  familyFromGraphql,
  familyToGraphql,
} from '../../hooks/chat/hardwareFamily';

// ============================================================================
// Enums
// ============================================================================

export type { HardwareFamily };
export { HARDWARE_FAMILIES, FAMILY_LABELS, familyFromGraphql, familyToGraphql };

/** The families the sheet's applies-to column names: the picker narrows only for these. */
export type FilteringFamily = 'kayak' | 'bike' | 'lawn_game';
const FILTERING_FAMILIES: FilteringFamily[] = ['kayak', 'bike', 'lawn_game'];

const FILTER_CAPTIONS: Record<FilteringFamily, string> = {
  kayak: 'kayak rentals',
  bike: 'bike rentals',
  lawn_game: 'lawn game rentals',
};

export function isFilteringFamily(
  family: HardwareFamily | null | undefined
): family is FilteringFamily {
  return Boolean(family) && FILTERING_FAMILIES.includes(family as FilteringFamily);
}

export type ChatTagSection = 'rider_support' | 'partner_support';
export type ChatTagCategoryKind = 'topic' | 'marker';

const sectionFromGraphql = (value: string): ChatTagSection =>
  value.toLowerCase() === 'partner_support' ? 'partner_support' : 'rider_support';

const kindFromGraphql = (value: string): ChatTagCategoryKind =>
  value.toLowerCase() === 'marker' ? 'marker' : 'topic';

// ============================================================================
// The glossary
// ============================================================================

export interface ChatTag {
  /** ant's row id, what the glossary mutations take. */
  id: string;
  /** The stable id, what a session stores: `<category slug>.<label slug>`. */
  slug: string;
  categoryId: string;
  label: string;
  definition: string | null;
  notes: string | null;
  /** Empty means every rental. */
  appliesTo: HardwareFamily[];
  position: number;
  /** Phase-2 hook: the app's report id this tag stands for; nothing reads it yet. */
  riderReportTagId: string | null;
  /** Retired when set: not offered, kept on the sessions that carry it. */
  archivedAt: string | null;
}

export interface ChatTagCategory {
  id: string;
  slug: string;
  section: ChatTagSection;
  name: string;
  description: string | null;
  notes: string | null;
  kind: ChatTagCategoryKind;
  position: number;
  archivedAt: string | null;
  /** In order: position, label, id. Retired tags included. */
  tags: ChatTag[];
}

export interface ChatTagGlossary {
  /** ant's rule (in phase 1, a Movatic admin), never the console's. */
  canEdit: boolean;
  /** Rider-support categories in sheet order (position, name, id), retired included. */
  categories: ChatTagCategory[];
  tagsBySlug: Map<string, ChatTag>;
  categoriesById: Map<string, ChatTagCategory>;
}

const byPositionThen = (a: string, b: string) => (x: number, y: number) =>
  x - y || a.localeCompare(b);

function mapTag(tag: AntChatTag): ChatTag {
  return {
    id: tag.id,
    slug: tag.slug,
    categoryId: tag.categoryId,
    label: tag.label,
    definition: tag.definition ?? null,
    notes: tag.notes ?? null,
    appliesTo: (tag.appliesTo ?? [])
      .map(familyFromGraphql)
      .filter((f): f is HardwareFamily => f !== null),
    position: tag.position,
    riderReportTagId: tag.riderReportTagId ?? null,
    archivedAt: tag.archivedAt ?? null,
  };
}

function mapCategory(category: AntChatTagCategory): ChatTagCategory {
  const tags = (category.tags ?? []).map(mapTag);
  tags.sort(
    (a, b) =>
      byPositionThen(a.label, b.label)(a.position, b.position) || a.id.localeCompare(b.id)
  );
  return {
    id: category.id,
    slug: category.slug,
    section: sectionFromGraphql(category.section),
    name: category.name,
    description: category.description ?? null,
    notes: category.notes ?? null,
    kind: kindFromGraphql(category.kind),
    position: category.position,
    archivedAt: category.archivedAt ?? null,
    tags,
  };
}

/**
 * ant's answer as the console holds it: enums folded, rider support only
 * (partner support is seeded but shown by nobody in phase 1), everything in
 * sheet order, and the two lookup maps every page needs.
 */
export function buildGlossary(raw: AntChatTagGlossary): ChatTagGlossary {
  const categories = raw.categories
    .map(mapCategory)
    .filter((c) => c.section === 'rider_support')
    .sort(
      (a, b) =>
        byPositionThen(a.name, b.name)(a.position, b.position) || a.id.localeCompare(b.id)
    );
  const tagsBySlug = new Map<string, ChatTag>();
  const categoriesById = new Map<string, ChatTagCategory>();
  categories.forEach((category) => {
    categoriesById.set(category.id, category);
    category.tags.forEach((tag) => tagsBySlug.set(tag.slug, tag));
  });
  return { canEdit: raw.canEdit, categories, tagsBySlug, categoriesById };
}

// ============================================================================
// Lookups on a stored tag id
// ============================================================================

/**
 * The strings earlier consoles put on sessions, with today's labels: the 15
 * presets of the old chatTags.ts and the older `unlock`, which had no label
 * and showed as its raw id. Shown, counted and removable; never re-added.
 */
export const LEGACY_CHAT_TAG_LABELS: Record<string, string> = {
  billing: 'Billing',
  hardware: 'Hardware',
  'app-issue': 'App issue',
  rental: 'Rental',
  other: 'Other',
  failed_to_unlock: 'Failed to unlock',
  equipment_condition: "Equipment's condition",
  unsafe_to_use: 'Unsafe to use',
  unsafe_for_next_user: 'Unsafe for the next user',
  cosmetic_damage: 'Cosmetic damage',
  hull_crack: 'Hull crack or hole',
  missing_accessories: 'Missing accessories',
  missing_life_jacket: 'Missing life jacket',
  wrong_equipment: 'Wrong equipment',
  damage_other: 'Other damage',
  unlock: 'Unlock (legacy)',
};

/** A glossary id holds exactly one `.`; no old string holds any. */
export function isGlossaryId(id: string): boolean {
  const dot = id.indexOf('.');
  return dot > 0 && dot < id.length - 1 && id.indexOf('.', dot + 1) === -1;
}

/** The label for any stored id: the glossary's (retired included), the legacy map's, else the id itself. */
export function tagLabel(
  glossary: ChatTagGlossary | null | undefined,
  id: string
): string {
  return glossary?.tagsBySlug.get(id)?.label ?? LEGACY_CHAT_TAG_LABELS[id] ?? id;
}

/** The category of a glossary id, or null (old strings never resolve to one). */
export function tagCategory(
  glossary: ChatTagGlossary | null | undefined,
  id: string
): ChatTagCategory | null {
  const tag = glossary?.tagsBySlug.get(id);
  return tag ? glossary?.categoriesById.get(tag.categoryId) ?? null : null;
}

export type TagStatus = 'active' | 'retired' | 'legacy' | 'unknown';

/** Active or retired (the tag or its category) for a glossary id; legacy for an old string; unknown otherwise. */
export function tagStatus(
  glossary: ChatTagGlossary | null | undefined,
  id: string
): TagStatus {
  const tag = glossary?.tagsBySlug.get(id);
  if (tag) {
    const category = glossary?.categoriesById.get(tag.categoryId);
    return tag.archivedAt || category?.archivedAt ? 'retired' : 'active';
  }
  return id in LEGACY_CHAT_TAG_LABELS ? 'legacy' : 'unknown';
}

// ============================================================================
// The picker's options
// ============================================================================

export interface PickerOption {
  tag: ChatTag;
  category: ChatTagCategory;
}

/**
 * The tags an agent may add: active, in an active category, in category
 * order then tag order. For a kayak, bike or lawn game session the tags
 * that apply to every rental or to that family, unless `showAll`; for any
 * other family, or none, every tag (never hide a tag because the family is
 * missing or the sheet does not cover it).
 */
export function pickerOptions(
  glossary: ChatTagGlossary,
  family: HardwareFamily | null | undefined,
  showAll: boolean
): PickerOption[] {
  const narrowTo = !showAll && isFilteringFamily(family) ? family : null;
  const options: PickerOption[] = [];
  glossary.categories.forEach((category) => {
    if (category.archivedAt) return;
    category.tags.forEach((tag) => {
      if (tag.archivedAt) return;
      if (narrowTo && tag.appliesTo.length > 0 && !tag.appliesTo.includes(narrowTo))
        return;
      options.push({ tag, category });
    });
  });
  return options;
}

/** What the picker says under its input about the family filter. */
export function familyCaption(
  family: HardwareFamily | null | undefined,
  showAll: boolean
): string {
  if (!isFilteringFamily(family)) return 'Rental type unknown: showing every tag';
  return showAll ? 'Showing every tag' : `Showing tags for ${FILTER_CAPTIONS[family]}`;
}

// ============================================================================
// Search
// ============================================================================

/**
 * Case and accents folded, straight and curly apostrophes dropped (so "wont"
 * finds "won’t"), en and em dashes to "-", spaces collapsed.
 */
export function foldSearch(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['‘’]/g, '')
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

const RANKS = ['label', 'category', 'definition'] as const;

function rankOf(option: PickerOption, words: string[]): number {
  const fields: Record<typeof RANKS[number], string> = {
    label: foldSearch(option.tag.label),
    category: foldSearch(option.category.name),
    definition: foldSearch(option.tag.definition ?? ''),
  };
  return RANKS.findIndex((rank) => words.every((word) => fields[rank].includes(word)));
}

/**
 * The options every word of `query` matches in one field: label hits first,
 * then category-name hits, then definition hits, glossary order within each.
 * The options themselves for a blank query.
 */
export function searchTags(options: PickerOption[], query: string): PickerOption[] {
  const words = foldSearch(query).split(' ').filter(Boolean);
  if (words.length === 0) return options;
  const ranked = options
    .map((option, index) => ({ option, index, rank: rankOf(option, words) }))
    .filter((entry) => entry.rank !== -1);
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
  return ranked.map((entry) => entry.option);
}

// ============================================================================
// Ids, as ant computes them (Ant.Chat.Changes.TagSlug)
// ============================================================================

/** The slug fold with no cut: what ant stores as normalized_label. */
export function normalizedLabel(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['‘’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** One part of an id, cut at a word boundary (a `-`) to at most `max` characters. */
export function slugPart(text: string, max: number): string {
  const slug = normalizedLabel(text);
  if (slug.length <= max) return slug;
  const head = slug.slice(0, max);
  if (slug[max] === '-') return head;
  const boundary = head.lastIndexOf('-');
  return boundary > 0 ? head.slice(0, boundary) : head;
}

/** ant's limits: 60 for the category part, 90 for the label part. */
const CATEGORY_PART_MAX = 60;
const LABEL_PART_MAX = 90;

/**
 * The id ant will give a new tag with `label` in `category`: the category's
 * slug, a dot, the label's slug, and `-2`, `-3`, ... when a tag of the
 * category (retired ones included) already holds it.
 */
export function tagIdPreview(category: ChatTagCategory, label: string): string {
  const base = `${slugPart(category.slug, CATEGORY_PART_MAX)}.${slugPart(
    label,
    LABEL_PART_MAX
  )}`;
  const taken = new Set(category.tags.map((t) => t.slug));
  if (!taken.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * An active tag of the category whose folded label equals `label`'s (what
 * ant's unique_active_label would refuse), other than `excludeId`.
 */
export function similarActiveTag(
  category: ChatTagCategory,
  label: string,
  excludeId?: string
): ChatTag | null {
  const folded = normalizedLabel(label);
  if (!folded) return null;
  return (
    category.tags.find(
      (t) => !t.archivedAt && t.id !== excludeId && normalizedLabel(t.label) === folded
    ) ?? null
  );
}

/**
 * The ids the app's reports carry (movatic-app package/config/issueReportingConfig.ts),
 * for the rider report id datalist of the glossary page. `safe_to_go` is a
 * rider answer, never a report, so it is not here.
 */
export const RIDER_REPORT_TAG_IDS: string[] = [
  'failed_to_unlock',
  'equipment_condition',
  'unsafe_to_use',
  'wrong_equipment',
  'changed_my_mind',
  'short_trip',
  'hull_crack',
  'missing_accessories',
  'damage_other',
  'cosmetic_damage',
  'unsafe_for_next_user',
  'missing_life_jacket',
];

// ============================================================================
// The hook
// ============================================================================

export const CHAT_TAG_GLOSSARY_KEY = ['chatTagGlossary'] as const;

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/**
 * The glossary, read once per page load and again when the window regains
 * focus (glossary edits publish no subscription). The glossary page
 * invalidates it after its own edits (`refetch`).
 */
export function useChatTagGlossary() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: CHAT_TAG_GLOSSARY_KEY,
    queryFn: async () => buildGlossary(await fetchChatTagGlossary()),
    staleTime: Infinity,
    refetchOnWindowFocus: 'always',
  });
  const refetch = useCallback(
    () => queryClient.invalidateQueries({ queryKey: CHAT_TAG_GLOSSARY_KEY }),
    [queryClient]
  );
  return {
    glossary: query.data ?? null,
    canEdit: query.data?.canEdit ?? false,
    loading: query.isLoading,
    error: query.error ? errorText(query.error) : null,
    refetch,
  };
}
